import { request, requestWithMsg, MsgResult } from './request';

// ★设计A (修改清单附录B): 加工模式字典化 API.
// 归属组 mm_group (参数真实键) / 模式值 mm_mode (对外契约) / 设备引用 mm_device_mode.

export interface MMGroup {
    id: number;
    uuid: string;
    code: string;
    nameZh: string;
    nameEn: string;
    /** 适用工艺框架 (process_arch.code); 空/null = 不限框架 (2026-10-22 取代 headType) */
    archCodes?: string[] | null;
    /** @deprecated 旧加工头类型, 已由 archCodes 取代, 仅只读透出 */
    headType?: string;
    sort: number;
    isEnabled: boolean;
    createdAt?: number;
    updatedAt?: number;
}

export interface MMMode {
    id: number;
    uuid: string;
    value: string;
    nameZh: string;
    nameEn: string;
    groupCode: string;
    sort: number;
    isEnabled: boolean;
    createdAt?: number;
    updatedAt?: number;
}

export interface MMDeviceRef {
    id: number;
    deviceKey: string;
    modeValue: string;
    enabled: boolean;
    aliasZh: string;
    aliasEn: string;
    speedMin: number | null;
    speedMax: number | null;
    sort: number;
}

const BASE = '/v1';

export function listMMGroups(includeDisabled = false): Promise<MMGroup[]> {
    return request<MMGroup[]>(`${BASE}/mmode/groups`, {
        query: { includeDisabled: includeDisabled ? 'true' : '' },
    });
}

export function createMMGroup(payload: Partial<MMGroup> & { code: string }): Promise<MsgResult<{ id?: number }>> {
    return requestWithMsg(`${BASE}/mmode/groups`, { method: 'POST', body: payload });
}

export function updateMMGroup(code: string, payload: Partial<MMGroup>): Promise<MsgResult<null>> {
    return requestWithMsg(`${BASE}/mmode/groups/update`, { method: 'POST', body: { code, ...payload } });
}

export function deleteMMGroup(code: string): Promise<MsgResult<null>> {
    return requestWithMsg(`${BASE}/mmode/groups/delete`, { method: 'POST', body: { code } });
}

export function listMMModes(group = '', includeDisabled = false): Promise<MMMode[]> {
    return request<MMMode[]>(`${BASE}/mmode/modes`, {
        query: { group, includeDisabled: includeDisabled ? 'true' : '' },
    });
}

export function createMMMode(payload: Partial<MMMode> & { value: string; groupCode: string }): Promise<MsgResult<{ id?: number }>> {
    return requestWithMsg(`${BASE}/mmode/modes`, { method: 'POST', body: payload });
}

export function updateMMMode(value: string, payload: Partial<MMMode>): Promise<MsgResult<null>> {
    return requestWithMsg(`${BASE}/mmode/modes/update`, { method: 'POST', body: { value, ...payload } });
}

export function deleteMMMode(value: string): Promise<MsgResult<null>> {
    return requestWithMsg(`${BASE}/mmode/modes/delete`, { method: 'POST', body: { value } });
}

/** 字典侧只读反查: 某模式值被哪些设备引用. */
export function listMMModeDevices(value: string): Promise<MMDeviceRef[]> {
    return request<MMDeviceRef[]>(`${BASE}/mmode/modes/devices`, { method: 'POST', dedupe: true, body: { value } });
}

/** 设备侧: 某设备引用的模式集合. */
export function listMMDeviceRefs(deviceKey: string): Promise<MMDeviceRef[]> {
    return request<MMDeviceRef[]>(`${BASE}/mmode/device-refs`, { query: { deviceKey } });
}

export interface MMDeviceRefSaveItem {
    modeValue: string;
    enabled?: boolean;
    aliasZh?: string;
    aliasEn?: string;
    speedMin?: number | null;
    speedMax?: number | null;
    sort?: number;
}

/** 设备侧整体保存引用集合 (勾选式; 非 admin 走审批). */
export function saveMMDeviceRefs(deviceKey: string, refs: MMDeviceRefSaveItem[]): Promise<MsgResult<null>> {
    return requestWithMsg(`${BASE}/mmode/device-refs/save`, {
        method: 'POST',
        body: { deviceKey, refs },
    });
}

// ---------------------------------------------------------------------------
// 模式值 → 中文名 模块级缓存 (参-02 值级中文显示用; 全站一次请求)
// ---------------------------------------------------------------------------

let modesCache: Promise<MMMode[]> | null = null;

/** 全量模式值字典 (缓存). 失败返回空数组并允许下次重试. */
export function loadMModesCached(): Promise<MMMode[]> {
    if (!modesCache) {
        modesCache = listMMModes('', true).catch(() => {
            modesCache = null;
            return [] as MMMode[];
        });
    }
    return modesCache;
}

/** 模式值显示名: 中文名为空回退原值 (参-02 显示逻辑). */
export function mmModeLabel(m: Pick<MMMode, 'value' | 'nameZh'>): string {
    return m.nameZh ? `${m.nameZh} (${m.value})` : m.value;
}
